import { ChevronDown } from '@app/components/various/Icons';
import { PopoverSheet } from '@app/components/various/Popover/PopoverSheet';
import { getHasActiveNavigationItemChild } from '@app/logic/navigation';
import { useIsTouchScreen } from '@app/utils/hooks/useIsTouchScreen';
import { usePopover } from '@app/utils/hooks/usePopover';
import { Navigation } from '@typings';
import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHover } from 'react-laag';
import { useLocation } from 'react-router-dom';

import { NavigationMenuList } from './NavigationMenuList';

interface Props {
  item: Navigation.Item;
  isFolder?: boolean;
}

export const NavigationMenuDropdown = ({ children, item, isFolder = false }: React.PropsWithChildren<Props>) => {
  const popoverId = React.useId();
  const triggerId = React.useId();
  const { pathname } = useLocation();
  const { t } = useTranslation(['common']);
  const isTouchScreen = useIsTouchScreen();

  const triggerWrapperRef = React.useRef<HTMLButtonElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  const [isOpen, setIsOpen] = React.useState(false);
  const [isOver, hoverProps, resetHover] = useHover({ hideOnScroll: false });

  const isVisible = isTouchScreen ? isOpen : isOver || isOpen;
  const hasActivePageChild = getHasActiveNavigationItemChild(item, pathname);

  const { layerProps, triggerProps, layerSide, renderLayer } = usePopover({
    escapeHandlerEnabled: isOpen,
    handleDisappear: () => {
      setIsOpen(false);
    },
    layerProps: {
      container: triggerWrapperRef.current ?? undefined,
      isOpen: isVisible,
      placement: 'bottom-center',
    },
  });

  const handleTriggerClick = () => {
    setIsOpen(current => !current);
  };

  const handleBlur = (event: React.FocusEvent) => {
    if (listRef.current?.contains(event.relatedTarget) || triggerWrapperRef.current?.contains(event.relatedTarget)) {
      return;
    }

    setIsOpen(false);
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    resetHover();
  };

  const triggerActiveClassName = cx({
    'block h-10 border-b border-current focus-within:border-transparent': hasActivePageChild,
  });

  const buttonProps = {
    'aria-controls': popoverId,
    'aria-expanded': isVisible,
    'aria-label': t('common:more_in', { parent: item.label }),
    id: triggerId,
    onClick: handleTriggerClick,
    type: 'button',
  } as const;

  const folderTrigger = (
    <button
      {...triggerProps}
      {...buttonProps}
      className={cx('flex items-center gap-1 focus-visible:rounded-large', triggerActiveClassName, {
        'pointer-events-none': !isTouchScreen,
      })}
    >
      {children}
      <ChevronDown className={cx('transition-transform', { 'rotate-180': isVisible })} />
    </button>
  );

  const linkTrigger = (
    <span className={cx('flex items-center gap-1', triggerActiveClassName)} {...triggerProps}>
      {children}
      <button {...buttonProps} className={cx('rounded-full', { 'pointer-events-none': !isTouchScreen })}>
        <ChevronDown className={cx('transition-transform', { 'rotate-180': isVisible })} />
      </button>
    </span>
  );

  return (
    <>
      <span ref={triggerWrapperRef} className="cursor-pointer" onBlur={handleBlur} {...hoverProps}>
        {isFolder ? folderTrigger : linkTrigger}
      </span>
      {renderLayer(
        <div id={popoverId} aria-labelledby={triggerId} {...layerProps} {...hoverProps} className="pt-2">
          <PopoverSheet className="max-h-96 min-w-40 p-2" isOpen={isVisible} layerSide={layerSide}>
            <NavigationMenuList
              ref={listRef}
              items={item.type !== 'link' ? item.children ?? [] : []}
              onBlur={handleBlur}
              onLinkClick={handleLinkClick}
            />
          </PopoverSheet>
        </div>,
      )}
    </>
  );
};
